import type { Store } from '@warp-drive/core';
import type { ImmutableRequestInfo } from '@warp-drive/core/types/request';
import type { JsonApiDocument } from '@warp-drive/core/types/spec/json-api-raw';
import type {
  AdapterPayload,
  LegacyStoreCompat,
} from '@warp-drive/legacy/compat';

type SerializerRequestType =
  | 'findRecord'
  | 'queryRecord'
  | 'findAll'
  | 'findBelongsTo'
  | 'findHasMany'
  | 'findMany'
  | 'query'
  | 'createRecord'
  | 'deleteRecord'
  | 'updateRecord';

const SERIALIZER_REQUEST_TYPES = new Set<SerializerRequestType>([
  'findRecord',
  'queryRecord',
  'findAll',
  'findBelongsTo',
  'findHasMany',
  'findMany',
  'query',
  'createRecord',
  'deleteRecord',
  'updateRecord',
]);

/**
 * Temporary response-normalization bridge for resource types that still use
 * legacy Models and Serializers.
 *
 * ResourceSchema-backed types should eventually be normalized by a modern UMG
 * REST codec instead, leaving source-key and value transformation concerns to
 * their schemas.
 */
export function normalizeWithLegacySerializer(
  store: Store,
  payload: AdapterPayload,
  recordType: string,
  request: ImmutableRequestInfo,
): JsonApiDocument {
  const legacyStore = store as Store & LegacyStoreCompat;
  const serializer = legacyStore.serializerFor(recordType);

  if (!serializer) {
    throw new Error(
      `No legacy serializer could be resolved for resource type "${recordType}".`,
    );
  }

  return serializer.normalizeResponse(
    store,
    store.modelFor(recordType),
    payload,
    request.records?.[0]?.id ?? null,
    serializerRequestType(request),
  );
}

function serializerRequestType(
  request: ImmutableRequestInfo,
): SerializerRequestType {
  if (
    typeof request.op === 'string' &&
    SERIALIZER_REQUEST_TYPES.has(request.op as SerializerRequestType)
  ) {
    return request.op as SerializerRequestType;
  }

  throw new Error(
    `Legacy serializer normalization requires a supported request operation; received "${String(request.op)}".`,
  );
}
